<template>
  <div>
    <div class="page-header">
      <div>
        <h5 class="page-title">Contacts</h5>
        <p class="page-sub">Manage individual contacts and their relationships</p>
      </div>
      <div class="header-actions">
        <div class="export-wrap" ref="exportWrap">
          <button class="btn-export" @click="exportMenuOpen = !exportMenuOpen" :disabled="exporting">
            <font-awesome-icon icon="download" /> Export <font-awesome-icon icon="chevron-down" style="font-size:10px" />
          </button>
          <div v-if="exportMenuOpen" class="export-menu">
            <button class="export-opt" @click="openExportModal('csv')"><font-awesome-icon icon="file-csv" class="me-2" />Export CSV</button>
            <button class="export-opt" @click="openExportModal('pdf')"><font-awesome-icon icon="file-pdf" class="me-2" />Export PDF</button>
          </div>
        </div>
        <button v-if="permission?.create_contacts" class="btn-primary" @click="openCreate">
          <font-awesome-icon icon="plus" class="me-1" /> New Contact
        </button>
      </div>
    </div>

    <div class="filter-bar">
      <div class="per-page-wrap">
        <label class="per-page-label">Show</label>
        <select class="per-page-select" v-model.number="length" @change="page = 1; fetchContacts()">
          <option v-for="n in [5,10,20,50,100,200,500,1000]" :key="n" :value="n">{{ n }}</option>
        </select>
        <span class="per-page-label">per page</span>
      </div>
      <input v-model="search" @input="debouncedFetch" class="filter-input" placeholder="Search name, email, company..." />
      <input type="date" v-model="dateFrom" class="filter-input" style="max-width:160px" />
      <input type="date" v-model="dateTo" class="filter-input" style="max-width:160px" />
      <button class="btn-apply" @click="applyDateFilter">Apply</button>
      <button class="btn-clear" @click="clearDateFilter" v-if="dateFrom || dateTo">Clear</button>
    </div>

    <div class="card">
      <div v-if="loading" class="loading-state">
        <div class="spinner"></div><p>Loading contacts...</p>
      </div>
      <div v-else-if="!contacts.length" class="empty-state">
        <font-awesome-icon icon="address-book" class="empty-icon" />
        <p>No contacts found</p>
        <span>Add your first contact to get started</span>
      </div>
      <table v-else class="data-table">
        <thead>
          <tr><th>Name</th><th>Email</th><th>Phone</th><th>Company</th><th>Job Title</th><th>Created</th><th></th></tr>
        </thead>
        <tbody>
          <tr v-for="c in contacts" :key="c.id">
            <td class="name-col">{{ c.full_name || (c.first_name + ' ' + c.last_name).trim() || '—' }}</td>
            <td>{{ c.email ?? '—' }}</td>
            <td>{{ c.phone ?? '—' }}</td>
            <td>{{ c.company_name ?? '—' }}</td>
            <td>{{ c.job_title ?? '—' }}</td>
            <td>{{ fmtDate(c.created_at) }}</td>
            <td>
              <div class="row-actions">
                <button v-if="permission?.edit_contacts" class="btn-link btn-link--edit" @click="openEdit(c)">Edit</button>
                <button v-if="permission?.delete_contacts" class="btn-link btn-link--del" @click="confirmDelete(c)">Delete</button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
      <div v-if="meta" class="pagination-bar">
        <span class="pagination-info">{{ meta.from ?? 1 }}–{{ meta.to ?? meta.total }} of {{ meta.total }} records</span>
        <div class="pagination-controls">
          <button class="pg-btn" :disabled="meta.current_page <= 1" @click="changePage(1)">«</button>
          <button class="pg-btn" :disabled="meta.current_page <= 1" @click="changePage(meta.current_page - 1)">‹</button>
          <div class="pg-pages">
            <button v-for="p in pageRange" :key="p" class="pg-num" :class="{ 'pg-num--active': p === meta.current_page }" @click="changePage(p)">{{ p }}</button>
          </div>
          <button class="pg-btn" :disabled="meta.current_page >= meta.last_page" @click="changePage(meta.current_page + 1)">›</button>
          <button class="pg-btn" :disabled="meta.current_page >= meta.last_page" @click="changePage(meta.last_page)">»</button>
        </div>
      </div>
    </div>

    <div class="modal-overlay" :class="{ open: modalOpen }" @click.self="closeModal">
      <div class="modal-box">
        <div class="modal-header">
          <span class="modal-title">{{ editing ? 'Edit Contact' : 'New Contact' }}</span>
          <button class="modal-close" @click="closeModal"><font-awesome-icon icon="times" /></button>
        </div>
        <form @submit.prevent="submit">
          <div class="modal-body">
            <div class="form-row">
              <div class="form-group">
                <label class="form-label">First Name <span class="req">*</span></label>
                <input v-model="form.first_name" class="form-input" required />
              </div>
              <div class="form-group">
                <label class="form-label">Last Name <span class="req">*</span></label>
                <input v-model="form.last_name" class="form-input" required />
              </div>
            </div>
            <div class="form-row">
              <div class="form-group">
                <label class="form-label">Email</label>
                <input v-model="form.email" type="email" class="form-input" />
              </div>
              <div class="form-group">
                <label class="form-label">Phone</label>
                <input v-model="form.phone" class="form-input" />
              </div>
            </div>
            <div class="form-row">
              <div class="form-group">
                <label class="form-label">Company</label>
                <input v-model="form.company_name" class="form-input" />
              </div>
              <div class="form-group">
                <label class="form-label">Job Title</label>
                <input v-model="form.job_title" class="form-input" />
              </div>
            </div>
            <div class="form-group form-group--full">
              <label class="form-label">Notes</label>
              <textarea v-model="form.notes" class="form-input" rows="3"></textarea>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn-ghost" @click="closeModal">Cancel</button>
            <button type="submit" class="btn-primary" :disabled="submitting">
              {{ submitting ? 'Saving…' : (editing ? 'Save Changes' : 'Create Contact') }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <div class="modal-overlay" :class="{ open: !!deleteTarget }" @click.self="deleteTarget = null">
      <div class="modal-box modal-box--sm">
        <div class="modal-header">
          <span class="modal-title">Delete Contact</span>
          <button class="modal-close" @click="deleteTarget = null"><font-awesome-icon icon="times" /></button>
        </div>
        <div class="modal-body"><p>Delete <strong>{{ deleteTarget?.full_name }}</strong>? This cannot be undone.</p></div>
        <div class="modal-footer">
          <button class="btn-ghost" @click="deleteTarget = null">Cancel</button>
          <button class="btn-danger" @click="doDelete" :disabled="submitting">{{ submitting ? 'Deleting…' : 'Delete' }}</button>
        </div>
      </div>
    </div>
    <!-- Column-Picker Export Modal -->
    <transition name="fade">
      <div v-if="showExportModal" class="col-picker-backdrop" @click.self="showExportModal=false">
        <div class="col-picker-modal">
          <div class="col-picker-header">
            <span>Choose Columns to Export <span class="col-picker-badge">{{ selectedExportCols.length }}</span></span>
            <button class="col-picker-close" @click="showExportModal=false"><font-awesome-icon icon="times"/></button>
          </div>
          <div class="col-picker-body">
            <div class="col-picker-actions">
              <button class="col-picker-link" @click="selectedExportCols=exportColumnDefs.map(c=>c.key)">Select All</button>
              <button class="col-picker-link" @click="selectedExportCols=[]">Deselect All</button>
            </div>
            <div class="col-picker-grid">
              <label v-for="col in exportColumnDefs" :key="col.key" class="col-picker-item">
                <input type="checkbox" :value="col.key" v-model="selectedExportCols" />
                {{ col.label }}
              </label>
            </div>
          </div>
          <div class="col-picker-footer">
            <button class="btn-ghost" @click="showExportModal=false">Cancel</button>
            <button class="btn-primary" :disabled="!selectedExportCols.length||exporting" @click="doExport">
              <font-awesome-icon v-if="exporting" icon="spinner" spin class="me-1"/>
              Export {{ pendingExportFmt.toUpperCase() }}
            </button>
          </div>
        </div>
      </div>
    </transition>

  </div>
</template>

<script>
import axios from 'axios';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { useToast } from 'vue-toastification';
import { useStore } from 'vuex';

export default {
  setup() { return { toast: useToast(), store: useStore() }; },
  computed: {
    permission() { return this.store.state.getCurrentUserPermission; },
    pageRange() {
      if (!this.meta) return [];
      const cur = this.meta.current_page, last = this.meta.last_page;
      const range = [];
      for (let i = Math.max(1, cur - 2); i <= Math.min(last, cur + 2); i++) range.push(i);
      return range;
    },
  },
  data() {
    return {
      contacts: [], meta: null, loading: false, submitting: false,
      search: '', page: 1, length: 5,
      dateFrom: '', dateTo: '', exportMenuOpen: false, exporting: false,
      showExportModal: false, pendingExportFmt: 'csv', selectedExportCols: [],
      exportColumnDefs: [
        { key: 'name',         label: 'Name',      pdfWidth: 140 },
        { key: 'email',        label: 'Email',     pdfWidth: 170 },
        { key: 'phone',        label: 'Phone',     pdfWidth: 100 },
        { key: 'company_name', label: 'Company',   pdfWidth: 130 },
        { key: 'job_title',    label: 'Job Title', pdfWidth: 130 },
        { key: 'created_at',   label: 'Created',   pdfWidth: 91  },
      ],
      modalOpen: false, editing: null, deleteTarget: null,
      form: { first_name:'', last_name:'', email:'', phone:'', company_name:'', job_title:'', notes:'' },
      debounceTimer: null,
    };
  },
  mounted() { this.fetchContacts(); document.addEventListener('click', this.onOutsideClick); },
  beforeUnmount() { document.removeEventListener('click', this.onOutsideClick); },
  methods: {
    async fetchContacts() {
      this.loading = true;
      try {
        const { data } = await axios.get('/crm/contacts', { params: { search: this.search, from: this.dateFrom || undefined, to: this.dateTo || undefined, page: this.page, length: this.length } });
        this.contacts = data.data?.data ?? data.data ?? data ?? [];
        this.meta     = data.data?.meta ?? data.meta ?? null;
      } catch { this.toast.error('Failed to load contacts'); }
      finally { this.loading = false; }
    },
    debouncedFetch() {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => { this.page = 1; this.fetchContacts(); }, 400);
    },
    changePage(p) { this.page = p; this.fetchContacts(); },
    applyDateFilter() { this.page = 1; this.fetchContacts(); },
    clearDateFilter() { this.dateFrom = ''; this.dateTo = ''; this.page = 1; this.fetchContacts(); },
    openExportModal(format) {
      this.exportMenuOpen     = false;
      this.pendingExportFmt   = format;
      this.selectedExportCols = this.exportColumnDefs.map(c => c.key);
      this.showExportModal    = true;
    },
    getRowValue(r, key) {
      switch (key) {
        case 'name':         return ((r.first_name ?? '') + ' ' + (r.last_name ?? '')).trim() || '—';
        case 'email':        return r.email ?? '—';
        case 'phone':        return r.phone ?? '—';
        case 'company_name': return r.company_name ?? '—';
        case 'job_title':    return r.job_title ?? '—';
        case 'created_at':   return r.created_at ? new Date(r.created_at).toLocaleDateString('en-GB') : '—';
        default:             return '—';
      }
    },
    async doExport() {
      if (!this.selectedExportCols.length) return;
      this.exporting = true; this.showExportModal = false;
      try {
        const params = { page: 1, per_page: 10000 };
        if (this.search)   params.search = this.search;
        if (this.dateFrom) params.from   = this.dateFrom;
        if (this.dateTo)   params.to     = this.dateTo;
        const { data } = await axios.get('/crm/contacts', { params });
        const rows = data.data?.data ?? data.data ?? data ?? [];
        const cols = this.exportColumnDefs.filter(c => this.selectedExportCols.includes(c.key));
        const headers = cols.map(c => c.label);
        const body    = rows.map(r => cols.map(c => this.getRowValue(r, c.key)));
        const date    = new Date().toISOString().slice(0, 10);
        if (this.pendingExportFmt === 'csv') {
          const csv  = [headers, ...body].map(r => r.map(v => `"${String(v).replace(/"/g,'""')}"`).join(',')).join('\n');
          const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
          const url  = URL.createObjectURL(blob);
          const a    = document.createElement('a');
          a.href = url; a.download = `contacts-${date}.csv`; a.click(); URL.revokeObjectURL(url);
        } else {
          const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
          const usable = 761; const totalW = cols.reduce((s,c)=>s+c.pdfWidth,0); const scale = usable/totalW;
          const colStyles = {}; cols.forEach((c,i)=>{ colStyles[i]={cellWidth:c.pdfWidth*scale}; });
          doc.setFontSize(18); doc.setFont('helvetica','bold'); doc.setTextColor(26,43,69); doc.text('Contacts',40,46);
          doc.setFontSize(9); doc.setFont('helvetica','normal'); doc.setTextColor(122,144,176);
          doc.text(`Exported ${new Date().toLocaleDateString('en-GB')}   ·   ${rows.length} record(s)`,40,62);
          doc.autoTable({ startY:78, head:[headers], body,
            styles:{fontSize:7.5,cellPadding:3.5,textColor:[26,43,69],overflow:'ellipsize'},
            headStyles:{fillColor:[0,74,173],textColor:255,fontStyle:'bold',fontSize:7.5},
            alternateRowStyles:{fillColor:[248,250,252]}, columnStyles:colStyles, tableWidth:usable, margin:{left:40,right:40},
            didDrawPage(d){ doc.setFontSize(8); doc.setTextColor(192,202,214); doc.text(`Page ${d.pageNumber}`,doc.internal.pageSize.width/2,doc.internal.pageSize.height-16,{align:'center'}); },
          });
          doc.save(`contacts-${date}.pdf`);
        }
      } catch { this.toast?.error('Export failed. Please try again.'); }
      finally { this.exporting = false; }
    },
    onOutsideClick(e) {
      if (this.$refs.exportWrap && !this.$refs.exportWrap.contains(e.target)) this.exportMenuOpen = false;
    },
    openCreate() { this.editing = null; this.form = { first_name:'', last_name:'', email:'', phone:'', company_name:'', job_title:'', notes:'' }; this.modalOpen = true; },
    openEdit(c)  { this.editing = c.id; this.form = { ...c }; this.modalOpen = true; },
    closeModal() { this.modalOpen = false; },
    confirmDelete(c) { this.deleteTarget = c; },
    async submit() {
      this.submitting = true;
      try {
        if (this.editing) { await axios.put(`/crm/contacts/${this.editing}`, this.form); this.toast.success('Contact updated'); }
        else              { await axios.post('/crm/contacts', this.form); this.toast.success('Contact created'); }
        this.closeModal(); this.fetchContacts();
      } catch (e) { this.toast.error(e.response?.data?.message ?? 'Failed to save'); }
      finally { this.submitting = false; }
    },
    async doDelete() {
      this.submitting = true;
      try {
        await axios.delete(`/crm/contacts/${this.deleteTarget.id}`);
        this.toast.success('Contact deleted'); this.deleteTarget = null; this.fetchContacts();
      } catch { this.toast.error('Failed to delete'); }
      finally { this.submitting = false; }
    },
    fmtDate(v) { return new Date(v).toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'numeric' }); },
  }
};
</script>

<style scoped>
.page-header{display:flex;align-items:flex-start;justify-content:space-between;margin-bottom:20px}
.page-title{font-size:20px;font-weight:700;color:#1a2b45;margin:0 0 3px}
.page-sub{font-size:13px;color:#7a90b0;margin:0}
.filter-bar{display:flex;gap:10px;margin-bottom:16px;flex-wrap:wrap}
.filter-input{padding:9px 13px;border:1.5px solid #dde5f0;border-radius:8px;font-size:13px;color:#1a2b45;flex:1;min-width:200px;outline:none}
.filter-input:focus{border-color:#004AAD}
.btn-primary{background:#004AAD;color:#fff;border:none;padding:9px 18px;border-radius:8px;font-size:13px;font-weight:600;cursor:pointer;display:inline-flex;align-items:center;gap:6px}
.btn-primary:hover{background:#003b8e}
.btn-primary:disabled{opacity:.55;cursor:not-allowed}
.btn-ghost{background:#f4f7fb;color:#3d5068;border:1px solid #e4eaf2;padding:8px 16px;border-radius:8px;font-size:13px;font-weight:600;cursor:pointer}
.btn-danger{background:#e03535;color:#fff;border:none;padding:8px 16px;border-radius:8px;font-size:13px;font-weight:600;cursor:pointer}
.btn-danger:disabled{opacity:.55;cursor:not-allowed}
.card{background:#fff;border-radius:12px;border:1.5px solid #e8edf5;overflow:hidden}
.loading-state,.empty-state{display:flex;flex-direction:column;align-items:center;justify-content:center;padding:56px 24px;color:#7a90b0;gap:8px}
.empty-icon{font-size:36px;opacity:.4}
.spinner{width:28px;height:28px;border:3px solid #e8edf5;border-top-color:#004AAD;border-radius:50%;animation:spin .7s linear infinite}
@keyframes spin{to{transform:rotate(360deg)}}
.data-table{width:100%;border-collapse:collapse;font-size:13px}
.data-table th{background:#f4f7fb;font-weight:600;color:#5a6a85;padding:11px 14px;text-align:left;white-space:nowrap}
.data-table td{padding:11px 14px;border-top:1px solid #f0f4fa;color:#1a2b45;vertical-align:middle}
.data-table tbody tr:hover{background:#f9fbff}
.name-col{font-weight:600}
.row-actions{display:flex;gap:8px}
.btn-link{background:none;border:none;color:#004AAD;font-size:12.5px;cursor:pointer;padding:0;font-weight:600}
.btn-link--del{color:#e03535}
.per-page-wrap{display:flex;align-items:center;gap:8px;flex-shrink:0}
.per-page-label{font-size:13px;color:#5a6a85;white-space:nowrap}
.per-page-select{padding:7px 10px;border:1.5px solid #dde5f0;border-radius:8px;font-size:13px;color:#1a2b45;background:#fff;outline:none;cursor:pointer}
.per-page-select:focus{border-color:#004AAD}
.pagination-bar{display:flex;align-items:center;justify-content:space-between;padding:12px 16px;border-top:1px solid #f0f4fa;flex-wrap:wrap;gap:10px}
.pagination-info{font-size:12.5px;color:#7a90b0}
.pagination-controls{display:flex;align-items:center;gap:4px}
.pg-btn{padding:6px 10px;border:1.5px solid #dde5f0;border-radius:7px;background:#fff;color:#1a2b45;font-size:13px;cursor:pointer}
.pg-btn:disabled{opacity:.4;cursor:not-allowed}
.pg-pages{display:flex;gap:3px}
.pg-num{width:30px;height:30px;border:1.5px solid #dde5f0;border-radius:7px;background:#fff;color:#1a2b45;font-size:12.5px;cursor:pointer;display:flex;align-items:center;justify-content:center}
.pg-num:hover{border-color:#004AAD;color:#004AAD}
.pg-num--active{background:#004AAD;border-color:#004AAD;color:#fff}
.modal-overlay{position:fixed;inset:0;z-index:1050;display:flex;align-items:center;justify-content:center;background:rgba(15,25,50,.45);backdrop-filter:blur(3px);opacity:0;pointer-events:none;transition:opacity .2s;padding:16px}
.modal-overlay.open{opacity:1;pointer-events:all}
.modal-box{background:#fff;border-radius:14px;width:100%;max-width:600px;box-shadow:0 20px 60px rgba(0,0,0,.14);animation:slideUp .2s ease;max-height:90vh;overflow-y:auto}
.modal-box--sm{max-width:420px}
@keyframes slideUp{from{opacity:0;transform:translateY(16px)}to{opacity:1;transform:translateY(0)}}
.modal-header{display:flex;align-items:center;justify-content:space-between;padding:18px 22px;border-bottom:1px solid #e4eaf2}
.modal-title{font-size:16px;font-weight:700;color:#1a2b45}
.modal-close{width:28px;height:28px;border-radius:7px;border:1px solid #dde5f0;background:transparent;cursor:pointer;font-size:14px;color:#7a90b0;display:flex;align-items:center;justify-content:center}
.modal-body{padding:20px 22px}
.modal-footer{display:flex;justify-content:flex-end;gap:10px;padding:16px 22px;border-top:1px solid #f0f4fa}
.form-row{display:grid;grid-template-columns:1fr 1fr;gap:14px;margin-bottom:4px}
.form-group{display:flex;flex-direction:column;gap:5px;margin-bottom:14px}
.form-group--full{grid-column:1/-1}
.form-label{font-size:12.5px;font-weight:600;color:#1a2b45}
.form-input{padding:9px 12px;border:1.5px solid #dde5f0;border-radius:8px;font-size:13px;color:#1a2b45;outline:none;width:100%;font-family:inherit}
.form-input:focus{border-color:#004AAD;box-shadow:0 0 0 3px rgba(0,74,173,.07)}
.req{color:#e03535}
.me-1{margin-right:4px}
.header-actions{display:flex;align-items:center;gap:10px}
.export-wrap{position:relative}
.btn-export{background:#f4f7fb;color:#3d5068;border:1.5px solid #e4eaf2;padding:9px 14px;border-radius:8px;font-size:13px;font-weight:600;cursor:pointer;display:inline-flex;align-items:center;gap:5px;white-space:nowrap}
.btn-export:hover{background:#e8edf5}
.btn-export:disabled{opacity:.55;cursor:not-allowed}
.export-menu{position:absolute;top:calc(100% + 6px);right:0;background:#fff;border:1.5px solid #e4eaf2;border-radius:10px;box-shadow:0 6px 20px rgba(0,0,0,.1);z-index:200;min-width:160px;overflow:hidden}
.export-opt{display:flex;align-items:center;width:100%;padding:10px 14px;background:none;border:none;font-size:13px;color:#1a2b45;cursor:pointer;text-align:left}
.export-opt:hover{background:#f4f7fb}
.btn-apply{background:#004AAD;color:#fff;border:none;padding:9px 14px;border-radius:8px;font-size:13px;font-weight:600;cursor:pointer}
.btn-apply:hover{background:#003b8e}
.btn-clear{background:#f4f7fb;color:#3d5068;border:1.5px solid #e4eaf2;padding:9px 14px;border-radius:8px;font-size:13px;font-weight:600;cursor:pointer}
.btn-clear:hover{background:#e8edf5}
.me-2{margin-right:6px}
.col-picker-backdrop{position:fixed;inset:0;background:rgba(10,20,40,.45);z-index:1100;display:flex;align-items:center;justify-content:center}
.col-picker-modal{background:#fff;border-radius:14px;width:480px;max-width:95vw;box-shadow:0 8px 40px rgba(0,74,173,.18);overflow:hidden}
.col-picker-header{display:flex;align-items:center;justify-content:space-between;padding:18px 22px 14px;border-bottom:1.5px solid #eaeff6;font-size:15px;font-weight:700;color:#1a2b45}
.col-picker-badge{background:#004AAD;color:#fff;border-radius:20px;padding:1px 9px;font-size:12px;font-weight:700;margin-left:8px}
.col-picker-close{background:none;border:none;cursor:pointer;color:#7a90b0;font-size:16px;line-height:1}
.col-picker-body{padding:16px 22px}
.col-picker-actions{display:flex;gap:14px;margin-bottom:12px}
.col-picker-link{background:none;border:none;color:#004AAD;font-size:13px;font-weight:600;cursor:pointer;padding:0}
.col-picker-link:hover{text-decoration:underline}
.col-picker-grid{display:grid;grid-template-columns:1fr 1fr;gap:8px 16px}
.col-picker-item{display:flex;align-items:center;gap:8px;font-size:13.5px;color:#1a2b45;cursor:pointer}
.col-picker-item input[type=checkbox]{width:15px;height:15px;accent-color:#004AAD;cursor:pointer}
.col-picker-footer{display:flex;justify-content:flex-end;gap:10px;padding:14px 22px 18px;border-top:1.5px solid #eaeff6}
.fade-enter-active,.fade-leave-active{transition:opacity .18s ease}
.fade-enter,.fade-leave-to{opacity:0}
</style>
